import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import '../style/filter.less';
import { BackIcon, FilterMinusIcon, ResetIcon } from "../icons";

// Utility function to highlight matching text
const highlightMatch = (text: string, query: string): JSX.Element => {
  if (!query) return <span>{text}</span>;
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) => 
        part.toLowerCase() === query.toLowerCase() ? 
          <span key={index} style={{ textDecoration: 'underline', fontWeight: 'bold' }}>{part}</span> : 
          <span key={index}>{part}</span>
      )}
    </span>
  );
};

interface FilterComponentProps {
  close: (filterObj: FilterObject, keepOpen?: boolean) => void;
  isExpandClass: string;
  selectionFilter: FilterObject;
  multiSelectOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  tripGroupOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  passengerOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  recordLocatorOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  agentNameOptions?: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
}

interface SelectionList {
  label: string;
  value: string;
}

export interface FilterObject {
  selectionType: string;
  referenceNumber: object[];
  agentName: object[];
  tripGroup: object[];
  passengerName: object[];
}

export type FilterComponentRef = {
  searchResults: () => FilterObject;
  getFilterCount: () => number;
};

const selectionLists: SelectionList[] = [
  {
    label: 'All',
    value: 'All'
  },
  {
    label: 'Trips with issues',
    value: 'Trips with issues'
  },
  {
    label: 'My issues',
    value: 'My issues'
  }
];

// Custom MultiSelect component
interface MultiSelectProps {
  label: string;
  options: Array<{
    tag: string;
    label: string;
    value: string;
    originalValue?: string; // Store original backend value with proper casing
  }>;
  selectedOptions: Array<any>;
  onChange: (selected: Array<any>) => void;
  resetKey?: number;
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ label, options, selectedOptions, onChange, resetKey, disabled = false }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<Array<any>>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isScrollable, setIsScrollable] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  // Clear input and close dropdown when resetKey changes
  useEffect(() => {
    setSearchValue('');
    setIsOpen(false);
  }, [resetKey]);

  // Check if content is scrollable
  useEffect(() => {
    if (selectedRef.current) {
      const { scrollHeight, clientHeight } = selectedRef.current;
      setIsScrollable(scrollHeight > clientHeight);
    }
  }, [selectedOptions]);

  // Determine size class based on number of selected options
  const getSizeClass = () => {
    if (selectedOptions.length === 0) return 'empty';
    if (selectedOptions.length <= 2) return 'small';
    if (selectedOptions.length <= 4) return 'medium';
    return 'large';
  };

  // Filter available options to exclude already selected ones
  useEffect(() => {
    const selectedValues = selectedOptions.map(option => option.value);
    const availableOptions = options.filter(option => !selectedValues.includes(option.value));
    
    // If there's a search value, filter by it; otherwise show all available options
    if (searchValue) {
      const filtered = availableOptions.filter(
        option => (option.originalValue || option.label).toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(availableOptions);
    }
  }, [options, selectedOptions, searchValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: any) => {
    if (disabled) return;
    onChange([...selectedOptions, option]);
    setSearchValue('');
    setIsOpen(false);
  };

  const handleRemove = (option: any) => {
    if (disabled) return;
    onChange(selectedOptions.filter(item => item.value !== option.value));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // Always filter from the original available options (excluding selected ones)
    const selectedValues = selectedOptions.map(option => option.value);
    const availableOptions = options.filter(option => !selectedValues.includes(option.value));
    
    if (value) {
      const filtered = availableOptions.filter(
        option => (option.originalValue || option.label).toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      // Reset to show all available options when search is cleared
      setFilteredOptions(availableOptions);
    }
  };

  return (
    <div className={`custom-multiselect ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div className="multiselect-label">{label}</div>
      <div className={`multiselect-container ${isScrollable ? 'scrollable' : ''} ${disabled ? 'disabled' : ''}`}>
        {/* Selected Options Pills */}
        <div 
          className={`multiselect-selected ${getSizeClass()} ${disabled ? 'disabled' : ''}`} 
          ref={selectedRef}
          onClick={() => !disabled && setIsOpen(true)}
        >
          {selectedOptions.map(option => (
            <div 
              key={option.value} 
              className="multiselect-pill"
              title={option.originalValue || option.label}
            >
              <span className="pill-text">{option.originalValue || option.label}</span>
              <button 
                className="pill-remove"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent opening dropdown when removing pill
                  handleRemove(option);
                }}
                disabled={disabled}
              >
                ×
              </button>
            </div>
          ))}
          <input 
            type="text"
            className="multiselect-input"
            placeholder={selectedOptions.length === 0 ? `Select ${label}...` : ""}
            onClick={() => !disabled && setIsOpen(true)}
            onFocus={() => !disabled && setIsOpen(true)}
            onChange={handleSearch}
            value={searchValue}
            disabled={disabled}
          />
        </div>
        
        {/* Dropdown */}
        {isOpen && filteredOptions.length > 0 && !disabled && (
          <div className="multiselect-dropdown scrollContent">
            {filteredOptions.map(option => (
              <div
                key={option.value}
                className="option"
                onClick={() => handleSelect(option)}
              >
                {searchValue ? highlightMatch(option.originalValue || option.label, searchValue) : (option.originalValue || option.label)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom Radio Group Component
interface RadioGroupProps {
  options: SelectionList[];
  value: string;
  onChange: (value: string) => void;
}

const RadioGroup: React.FC<RadioGroupProps> = ({ options, value, onChange }) => {
  return (
    <div className="custom-radio-group">
      {options.map((option) => (
        <label key={option.value} className="custom-radio">
          <input
            type="radio"
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="radio-label">{option.label}</span>
        </label>
      ))}
    </div>
  );
};

// Custom Text Input Component
interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suggestions?: Array<{tag: string, label: string, value: string, originalValue?: string}>;
}

const TextInput: React.FC<TextInputProps> = ({ label, value, onChange, suggestions }) => {
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Array<{tag: string, label: string, value: string, originalValue?: string}>>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Filter suggestions based on input
  useEffect(() => {
    if (suggestions && value) {
      const filtered = suggestions.filter(
        suggestion => (suggestion.originalValue || suggestion.value).toLowerCase().includes(value.toLowerCase())
      ).slice(0, 10); // Limit to 10 suggestions for performance
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions([]);
    }
  }, [value, suggestions]);
  
  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <div className="custom-text-input" ref={inputRef}>
      <label className="input-label">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => setShowSuggestions(true)}
        className="text-input"
        placeholder={`Enter ${label}...`}
      />
      
      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="text-suggestions">
          {filteredSuggestions.map((suggestion, index) => (
            <div 
              key={index} 
              className="suggestion-item"
              onClick={() => {
                onChange(suggestion.originalValue || suggestion.value);
                setShowSuggestions(false);
              }}
            >
              {value ? highlightMatch(suggestion.originalValue || suggestion.label, value) : (suggestion.originalValue || suggestion.label)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

  const FilterComponent = forwardRef<FilterComponentRef, FilterComponentProps>(
  ({ close, isExpandClass, selectionFilter, multiSelectOptions = [], tripGroupOptions = [], passengerOptions = [], recordLocatorOptions = [], agentNameOptions = [] }, ref) => {
    const [statusSection, setStatusSection] = useState<boolean>(true);
    const [passengerSection, setPassengerSection] = useState<boolean>(true);
    const [assignedSection, setAssignedSection] = useState<boolean>(true);
    
    // Define state setters that update parent component immediately
    const [selectionType, setSelectionTypeState] = useState<string>(selectionFilter.selectionType);
    const [referenceNumber, setReferenceNumberState] = useState<object[]>(selectionFilter.referenceNumber);
    const [agentName, setAgentNameState] = useState<object[]>(selectionFilter.agentName);
    const [tripGroup, setTripGroupState] = useState<object[]>(selectionFilter.tripGroup);
    const [passengerName, setPassengerNameState] = useState<object[]>(selectionFilter.passengerName);
    // Add resetKey to force reset of child inputs
    const [resetKey, setResetKey] = useState<number>(0);
    
    // Define wrapper functions to update state and notify parent
    const setSelectionType = (value: string) => {
      setSelectionTypeState(value);
      const filterObj = {
        selectionType: value,
        referenceNumber,
        agentName,
        tripGroup,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };
    
    const setReferenceNumber = (value: object[]) => {
      setReferenceNumberState(value);
      const filterObj = {
        selectionType,
        referenceNumber: value,
        agentName,
        tripGroup,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };
    
    const setAgentName = (value: object[]) => {
      setAgentNameState(value);
      const filterObj = {
        selectionType,
        referenceNumber,
        agentName: value,
        tripGroup,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };
    
    const setTripGroup = (value: object[]) => {
      setTripGroupState(value);
      const filterObj = {
        selectionType,
        referenceNumber,
        agentName,
        tripGroup: value,
        passengerName
      };
      close(filterObj, true); // Keep panel open
    };
    
    const setPassengerName = (value: object[]) => {
      setPassengerNameState(value);
      const filterObj = {
        selectionType,
        referenceNumber,
        agentName,
        tripGroup,
        passengerName: value
      };
      close(filterObj, true); // Keep panel open
    };
    
    // Handle reset function to clear all filters without closing the panel
    const handleReset = () => {
      // Reset all filter values to default by updating the parent directly
      // This will trigger a re-render with updated values
      const resetObj = {
        selectionType: 'All',
        referenceNumber: [],
        agentName: [],
        tripGroup: [],
        passengerName: []
      };
      // Update local state
      setSelectionTypeState('All');
      setReferenceNumberState([]);
      setAgentNameState([]);
      setTripGroupState([]);
      setPassengerNameState([]);
      // Increment resetKey to force child input reset
      setResetKey(prev => prev + 1);
      // Call close with keepOpen=true to prevent panel from closing
      close(resetObj, true);
    };
    
    // Use separate options or fall back to multiSelectOptions for backward compatibility
    const effectiveTripGroupOptions = tripGroupOptions.length > 0 ? tripGroupOptions : multiSelectOptions;
    const effectivePassengerOptions = passengerOptions.length > 0 ? passengerOptions : [];

    // Handle click outside to close the filter panel only (no need to reapply filters)
    const filterRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
          // Just close the panel without sending filter updates again
          // Pass the current selectionFilter instead of recalculating to prevent filter reapplication
          close(selectionFilter, false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [selectionFilter]);

    const headerTemplate = (label: string, setState: React.Dispatch<React.SetStateAction<boolean>>, state: boolean) => {
      return (
        <div className='sectionHeader'>
          <div className="sectionText">{label}</div>
          <div className="sectionIcon" onClick={() => setState(!state)}>
            <FilterMinusIcon />
          </div>
        </div>
      );
    };

    const searchResults = () => {
      // Return the current filter state
      let obj = {
        selectionType,
        referenceNumber,
        agentName,
        tripGroup,
        passengerName
      };
      return obj;
    };

    const getFilterCount = () => {
      const filterObj = searchResults();
      // Start with 1 by default (because "ALL" is selected)
      let count = 1;
      
      // Count each selected value in arrays
      if (filterObj.tripGroup && Array.isArray(filterObj.tripGroup) && filterObj.tripGroup.length > 0) {
        count += filterObj.tripGroup.length;
      }
      
      if (filterObj.passengerName && Array.isArray(filterObj.passengerName) && filterObj.passengerName.length > 0) {
        count += filterObj.passengerName.length;
      }
      
      if (filterObj.referenceNumber && Array.isArray(filterObj.referenceNumber) && filterObj.referenceNumber.length > 0) {
        count += filterObj.referenceNumber.length;
      }
      
      if (filterObj.agentName && Array.isArray(filterObj.agentName) && filterObj.agentName.length > 0) {
        count += filterObj.agentName.length;
      }
      
      // If selection type is not "All", add 1 more to the count
      if (filterObj.selectionType && filterObj.selectionType !== 'All') {
        count += 1;
      }
      
      return count;
    };

    useImperativeHandle(ref, () => ({
      searchResults,
      getFilterCount
    }));

    // Add custom styles for scrollbar
    useEffect(() => {
      // Add scrollbar styles if not already present
      if (!document.querySelector("style[data-scroll-content]")) {
        const style = document.createElement("style");
        style.setAttribute("data-scroll-content", "true");
        style.textContent = `
          .scrollContent::-webkit-scrollbar,
          .multiselect-selected::-webkit-scrollbar {
            width: 3px;
          }
          .scrollContent::-webkit-scrollbar-track,
          .multiselect-selected::-webkit-scrollbar-track {
            background: transparent;
          }
          .scrollContent::-webkit-scrollbar-thumb,
          .multiselect-selected::-webkit-scrollbar-thumb {
            background: #4B5F7A;
            border-radius: 4px;
          }
          .scrollContent::-webkit-scrollbar-thumb:hover,
          .multiselect-selected::-webkit-scrollbar-thumb:hover {
            background: #3a4b60;
          }
          .floating-tooltip {
            position: absolute;
            background-color: #2b3a4e;
            color: white;
            padding: 5px 10px;
            border-radius: 4px;
            z-index: 1000;
            transform: translateX(-50%) translateY(-100%);
            margin-top: -8px;
          }
          .tooltip-arrow {
            position: absolute;
            bottom: -5px;
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-top: 5px solid #2b3a4e;
            transform: translateX(-50%);
          }
          
          /* Custom Multi-Select Styles - Evolve-like */
          .custom-multiselect {
            margin-bottom: 10px;
            position: relative;
          }
          .custom-multiselect.disabled {
            opacity: 0.6;
          }
          .multiselect-label {
            color: #8998AB;
            margin-bottom: 6px;
            font-size: 12px;
            font-weight: normal;
          }
          .multiselect-container {
            background: #0D1117;
            border-radius: 4px;
            border: 1px solid #1B222C;
            box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.07);
          }
          .multiselect-container:focus-within {
            border-color: #4B5F7A;
            box-shadow: 0 0 0 1px rgba(75, 95, 122, 0.3);
          }
          .multiselect-container.disabled {
            background: #06080B;
            border-color: #0F1419;
            cursor: not-allowed;
          }
          .multiselect-container.disabled:focus-within {
            border-color: #0F1419;
            box-shadow: none;
          }
          .multiselect-container.scrollable {
            position: relative;
          }
          /* Removed the diagonal line indicator - causing visual issues */
          .multiselect-selected {
            min-height: 36px; /* Match Evolve height - default single line */
            padding: 4px 6px;
            display: flex;
            flex-wrap: wrap;
            align-items: center; /* Keep center alignment for single line */
            align-content: flex-start;
            overflow-x: hidden;
            gap: 2px;
            transition: all 0.2s ease; /* Smooth transitions */
          }
          
          /* Size variants based on content */
          .multiselect-selected.empty {
            height: 36px;
            max-height: 36px;
            overflow-y: hidden;
            align-items: center;
          }
          
          .multiselect-selected.small {
            min-height: 36px;
            max-height: 50px; /* Allow slight expansion */
            overflow-y: hidden;
            align-items: center;
          }
          
          .multiselect-selected.medium {
            min-height: 36px;
            max-height: 64px; /* Allow more expansion */
            overflow-y: auto;
            align-items: flex-start;
            padding: 4px 6px 6px 6px;
          }
          
          .multiselect-selected.large {
            min-height: 36px;
            max-height: 72px; /* Maximum expansion before scrolling */
            overflow-y: auto;
            align-items: flex-start;
            padding: 4px 6px 6px 6px;
          }
          .multiselect-pill {
            background: #1D2737;
            color: #FDFDFD;
            border-radius: 3px;
            padding: 2px 6px;
            margin: 1px; /* Reduce margin since we have gap */
            display: flex;
            align-items: center;
            max-width: 140px; /* Slightly reduce max width to fit better in scrollable area */
            overflow: hidden;
            position: relative;
            font-size: 13px;
            flex-shrink: 0; /* Prevent pills from shrinking */
          }
          .pill-text {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 13px;
          }
          .pill-remove {
            background: none;
            border: none;
            color: #98A6B8;
            cursor: pointer;
            font-size: 14px;
            padding: 0 0 0 4px;
            margin-left: 2px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .pill-remove:hover {
            color: #FDFDFD;
          }
          .pill-remove:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }
          .pill-remove:disabled:hover {
            color: #98A6B8;
          }
          .multiselect-input {
            background: transparent;
            border: none;
            color: #FDFDFD;
            flex-grow: 1;
            flex-shrink: 0; /* Prevent input from shrinking too much */
            min-width: 80px; /* Slightly increase minimum width */
            max-width: 100%; /* Ensure it doesn't exceed container */
            height: 28px;
            padding: 4px 8px;
            outline: none;
            font-size: 14px;
          }
          .multiselect-input:disabled {
            color: #4B5F7A;
            cursor: not-allowed;
          }
          .multiselect-dropdown {
            position: absolute;
            top: calc(100% + 2px);
            left: 0;
            right: 0;
            background: #0D1117;
            border: 1px solid #1B222C;
            border-radius: 4px;
            max-height: 200px;
            overflow-y: auto;
            z-index: 10;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          }
          .option {
            padding: 8px 10px;
            cursor: pointer;
            color: #8998AB;
            font-size: 13px;
            transition: background 0.2s;
          }
          .option:hover {
            background: #1D2737;
            color: #FDFDFD;
          }
          .no-options {
            padding: 8px 10px;
            color: #8998AB;
            font-style: italic;
            font-size: 13px;
          }
          
          /* Custom Radio Styles - Evolve-like */
          .custom-radio-group {
            display: flex;
            flex-direction: column;
          }
          .custom-radio {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            cursor: pointer;
            color: #98A6B8;
            position: relative;
            padding-left: 26px; /* Space for the custom radio circle */
          }
          .custom-radio input {
            position: absolute;
            opacity: 0; /* Hide the default radio button */
            cursor: pointer;
            height: 0;
            width: 0;
          }
          .custom-radio .radio-label::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            width: 18px;
            height: 18px;
            border: 1px solid #4B5F7A;
            border-radius: 50%;
            background: #0D1117;
          }
          .custom-radio input:checked + .radio-label::after {
            content: "";
            position: absolute;
            left: 5px;
            top: 5px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #4B5F7A;
          }
          .custom-radio input:checked + .radio-label {
            color: #FDFDFD; /* Slightly brighter text when selected */
          }
          .custom-radio .radio-label {
            font-size: 14px;
            line-height: 18px;
            font-weight: normal;
            color: #98A6B8;
          }
          
          /* Custom Text Input Styles - Evolve-like */
          .custom-text-input {
            margin-bottom: 10px;
          }
          .input-label {
            color: #8998AB;
            margin-bottom: 6px;
            display: block;
            font-size: 12px;
            font-weight: normal;
          }
          .text-input {
            width: 100%;
            padding: 8px 10px;
            background: #0D1117;
            border: 1px solid #1B222C;
            border-radius: 4px;
            color: #FDFDFD;
            outline: none;
            font-size: 14px;
            height: 36px; /* Consistent height like Evolve */
            box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.07);
          }
          .text-input:focus {
            border-color: #4B5F7A;
            box-shadow: 0 0 0 1px rgba(75, 95, 122, 0.3);
          }
          
          /* Evolve-like section styling */
          .filter-section {
            margin-bottom: 8px;
            border-bottom: 1px solid #1B222C;
            padding-bottom: 6px;
          }
          .filter-section:last-child {
            border-bottom: none;
          }
          .evolve-section-body {
            padding: 6px 0;
          }
          .evolve-form-group {
            margin-top: 10px;
          }
          .sectionHeader {
            border-bottom: 1px solid #1B222C;
            padding-bottom: 6px;
            margin-bottom: 2px;
          }
          .sectionText {
            font-weight: 500;
            color: #FDFDFD;
          }
          .sectionIcon {
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 3px;
          }
          .sectionIcon:hover {
            background: #1D2737;
          }
          
          /* Evolve-like header styling */
          .evolve-header {
            padding: 12px 14px;
            border-bottom: 1px solid #1B222C;
            display: flex;
            align-items: center;
            background: #0D1117;
          }
          .evolve-btn {
            padding: 6px;
            cursor: pointer;
            border-radius: 3px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .evolve-btn:hover {
            background: #1D2737;
          }
          .evolve-reset-btn {
            padding: 6px 10px;
            border-radius: 3px;
            cursor: pointer;
            display: flex;
            align-items: center;
            background: transparent;
            transition: background 0.2s;
          }
          .evolve-reset-btn:hover {
            background: #1D2737;
          }
          .filterText {
            font-weight: 500;
            font-size: 16px;
          }
          .filterResetText {
            font-size: 14px;
          }
        `;
        document.head.appendChild(style);
      }
    }, []);

    return (
      <div className={`filterLayer ${isExpandClass}`} ref={filterRef}>
        <div className='filterHeader evolve-header'>
          <div 
            className="filterBack evolve-btn" 
            onClick={() => {
              // Only close the panel without applying filters again
              close(selectionFilter, false); // Use current selectionFilter instead of recalculating
            }}
          >
            <BackIcon />
          </div>
          <div className="filterText">Filter</div>
          <div 
            className="filterParent evolve-reset-btn" 
            onClick={handleReset}
          >
            <div className="filterReset">
              <ResetIcon />
            </div>
            <div className="filterResetText">Reset</div>
          </div>
        </div>
        <div className='filterBody scrollContent'>
          <div className="statusSection filter-section">
            {headerTemplate('Status', setStatusSection, statusSection)}
            {statusSection && (
              <div className="sectionBody evolve-section-body">
                <div className="radioParent">
                  <RadioGroup
                    options={selectionLists}
                    value={selectionType}
                    onChange={setSelectionType}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="passengerSection filter-section">
            {headerTemplate('Passenger', setPassengerSection, passengerSection)}
            {passengerSection && (
              <div className="sectionBody evolve-section-body">
                <div className="multiselect">
                  <MultiSelect
                    label="Trip Group"
                    options={effectiveTripGroupOptions}
                    selectedOptions={tripGroup}
                    onChange={setTripGroup}
                    resetKey={resetKey}
                  />
                </div>
                <div className="multiselect evolve-form-group">
                  <MultiSelect
                    label="Passenger Name"
                    options={effectivePassengerOptions}
                    selectedOptions={passengerName}
                    onChange={setPassengerName}
                    resetKey={resetKey}
                  />
                </div>
                <div className="textfield evolve-form-group">
                  <MultiSelect
                    label="Reference Number"
                    options={recordLocatorOptions}
                    selectedOptions={referenceNumber}
                    onChange={setReferenceNumber}
                    resetKey={resetKey}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="assignedSection filter-section">
            {headerTemplate('Assigned to', setAssignedSection, assignedSection)}
            {assignedSection && (
              <div className="sectionBody evolve-section-body">
                <div className={`textfield ${selectionType === 'My issues' ? 'disabled' : ''}`}>
                  <MultiSelect
                    label="Agent Name"
                    options={agentNameOptions}
                    selectedOptions={agentName}
                    onChange={setAgentName}
                    resetKey={resetKey}
                    disabled={selectionType === 'My issues'}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default FilterComponent;